import { useState, useEffect } from 'react';
import { useSurveyProtection, IntegrityReport } from '@/hooks/useSurveyProtection';
import { SurveyHealthIndicator } from './SurveyHealthBadge';
import LoadingSpinner from './LoadingSpinner';
import { supabaseClient } from '../config/supabase';
import { AlertTriangle } from 'lucide-react';

interface Program {
  id: string;
  name: string;
  code: string;
}

interface Department {
  id: string;
  name: string;
}

interface DeploySurveyDialogProps {
  surveyId: string;
  surveyTitle: string;
  isOpen: boolean;
  isRedeployMode?: boolean;
  isSuperAdmin?: boolean;
  onClose: () => void;
  onConfirm: (deploymentOptions: {
    target_batch: number[];
    target_programs: string[];
    target_departments: string[];
    deadline: string | null;
  }) => void;
  programs: Program[];
  departments: Department[];
  availableYears: number[];
}

export function DeploySurveyDialog({
  surveyId,
  surveyTitle,
  isOpen,
  isRedeployMode = false,
  isSuperAdmin = true,
  onClose,
  onConfirm,
  programs,
  departments,
  availableYears,
}: DeploySurveyDialogProps) {
  const { getIntegrityReport, loading } = useSurveyProtection();
  const [report, setReport] = useState<IntegrityReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [existingResponseCount, setExistingResponseCount] = useState<number>(0);
  
  // Deployment options
  const [deployTarget, setDeployTarget] = useState<'all' | 'specific'>('all');
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [deployDeadline, setDeployDeadline] = useState<string>('');

  useEffect(() => {
    if (isOpen && surveyId) {
      checkSurveyHealth();
      checkExistingResponses();
      // Reset deployment options
      setDeployTarget('all');
      setSelectedYears([]);
      setSelectedPrograms([]);
      setSelectedDepartments([]);
      setDeployDeadline('');
    }
  }, [isOpen, surveyId]);

  const checkExistingResponses = async () => {
    try {
      const { count } = await supabaseClient
        .from('survey_responses')
        .select('*', { count: 'exact', head: true })
        .eq('survey_id', surveyId);
      
      setExistingResponseCount(count || 0);
    } catch (err) {
      console.error('Error checking existing responses:', err);
      setExistingResponseCount(0);
    }
  };

  const checkSurveyHealth = async () => {
    setError(null);
    try {
      const data = await getIntegrityReport(surveyId);
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check survey health');
    }
  };

  const canDeploy = report?.broken_references === 0;
  const healthStatus = report?.health_status.includes('HEALTHY')
    ? 'HEALTHY'
    : report?.health_status.includes('WARNING')
    ? 'WARNING'
    : 'CRITICAL';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Deploy Survey</h2>
          <p className="text-sm text-gray-600 mt-1">{surveyTitle}</p>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
              <button
                onClick={checkSurveyHealth}
                className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
              >
                Retry
              </button>
            </div>
          ) : report ? (
            <>
              {/* Health Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Survey Health Check
                </h3>
                <SurveyHealthIndicator
                  status={healthStatus}
                  brokenCount={report.broken_references}
                  showMessage={true}
                />
              </div>

              {/* Survey Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatItem label="Total Questions" value={report.total_questions} />
                <StatItem label="Rating Questions" value={report.rating_questions} />
                <StatItem label="PEO References" value={report.peo_references} />
                <StatItem
                  label="Broken Refs"
                  value={report.broken_references}
                  highlight={report.broken_references > 0 ? 'red' : 'green'}
                />
              </div>

              {/* Broken References Details */}
              {report.broken_references > 0 && report.broken_details && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-red-900 mb-2">
                    ⚠ Broken References Found
                  </h3>
                  <div className="space-y-2">
                    {report.broken_details.map((ref, idx) => (
                      <div
                        key={idx}
                        className="text-sm bg-white rounded p-2 border border-red-100"
                      >
                        <div className="font-medium text-gray-900">
                          Question: {ref.question_id}
                        </div>
                        <div className="text-gray-600">
                          PEO: {ref.peo_title || ref.missing_peo_id.substring(0, 8) + '...'}
                        </div>
                        <div className="text-red-600 text-xs mt-1">{ref.message}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-sm text-red-800">
                    <strong>Action required:</strong> Fix these broken references before
                    deploying. Either restore the deleted PEOs/POs or update the survey
                    questions.
                  </div>
                </div>
              )}

              {/* Deployment Warning/Success */}
              {canDeploy ? (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <span className="text-green-600 text-xl">✓</span>
                      <div>
                        <h3 className="text-sm font-medium text-green-900">
                          Ready to Deploy
                        </h3>
                        <p className="text-sm text-green-700 mt-1">
                          This survey has passed all validation checks and can be safely
                          deployed to alumni.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Warning about existing responses */}
                  {existingResponseCount > 0 && (
                    <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-orange-900 mb-1">
                            Warning: Existing Responses Will Be Deleted
                          </h3>
                          <p className="text-sm text-orange-800">
                            This survey has <strong>{existingResponseCount} existing response{existingResponseCount > 1 ? 's' : ''}</strong> that will be <strong>permanently deleted</strong> when you {isRedeployMode ? 'redeploy' : 'deploy'} this survey.
                          </p>
                          <p className="text-sm text-orange-700 mt-2">
                            {isRedeployMode 
                              ? 'Alumni who previously responded will be able to submit new responses to the updated survey.'
                              : 'This typically happens when reopening a closed survey. Old responses will be cleared to allow fresh submissions.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Deployment Options */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Deployment Options
                    </h3>

                    {/* Target Selection */}
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="deployTarget"
                          value="all"
                          checked={deployTarget === 'all'}
                          onChange={(e) => setDeployTarget(e.target.value as 'all' | 'specific')}
                          className="w-4 h-4 text-blue-600"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            All Alumni
                          </span>
                          <p className="text-xs text-gray-600">
                            {isSuperAdmin ? 'Deploy to all registered alumni' : 'Deploy to all alumni in your department(s)'}
                          </p>
                        </div>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="deployTarget"
                          value="specific"
                          checked={deployTarget === 'specific'}
                          onChange={(e) => setDeployTarget(e.target.value as 'all' | 'specific')}
                          className="w-4 h-4 text-blue-600"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            Specific Alumni
                          </span>
                          <p className="text-xs text-gray-600">
                            Target specific programs and/or graduation years
                          </p>
                        </div>
                      </label>
                    </div>

                    {/* Conditional Fields for Specific Targeting */}
                    {deployTarget === 'specific' && (
                      <div className="space-y-4 pl-6 border-l-2 border-blue-200">
                        {/* Programs */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Target Programs
                          </label>
                          <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-white rounded border border-gray-300">
                            {programs.map((program) => (
                              <label
                                key={program.id}
                                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedPrograms.includes(program.code)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedPrograms([...selectedPrograms, program.code]);
                                    } else {
                                      setSelectedPrograms(
                                        selectedPrograms.filter((p) => p !== program.code)
                                      );
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600"
                                />
                                <span className="text-sm text-gray-700">{program.code}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Departments */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Target Departments
                          </label>
                          <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-white rounded border border-gray-300">
                            {departments.map((dept) => (
                              <label
                                key={dept.id}
                                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedDepartments.includes(dept.name)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedDepartments([...selectedDepartments, dept.name]);
                                    } else {
                                      setSelectedDepartments(
                                        selectedDepartments.filter((d) => d !== dept.name)
                                      );
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600"
                                />
                                <span className="text-sm text-gray-700">{dept.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Graduation Years */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Target Graduation Years
                          </label>
                          <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 bg-white rounded border border-gray-300">
                            {availableYears.map((year) => (
                              <label
                                key={year}
                                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedYears.includes(year)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedYears([...selectedYears, year]);
                                    } else {
                                      setSelectedYears(
                                        selectedYears.filter((y) => y !== year)
                                      );
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600"
                                />
                                <span className="text-sm text-gray-700">{year}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Deadline */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Response Deadline (Optional)
                      </label>
                      <input
                        type="datetime-local"
                        value={deployDeadline}
                        onChange={(e) => setDeployDeadline(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Leave empty for no deadline
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <span className="text-red-600 text-xl">✕</span>
                    <div>
                      <h3 className="text-sm font-medium text-red-900">Cannot Deploy</h3>
                      <p className="text-sm text-red-700 mt-1">
                        This survey has broken PEO/PO references and cannot be deployed
                        until they are fixed.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          {canDeploy ? (
            <button
              onClick={() => {
                onConfirm({
                  target_batch: deployTarget === 'specific' ? selectedYears : [],
                  target_programs: deployTarget === 'specific' ? selectedPrograms : [],
                  target_departments: deployTarget === 'specific' ? selectedDepartments : [],
                  deadline: deployDeadline ? new Date(deployDeadline).toISOString() : null,
                });
                onClose();
              }}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Deploy Survey
            </button>
          ) : (
            <button
              disabled
              className="px-4 py-2 text-sm font-medium text-white bg-gray-400 rounded-md cursor-not-allowed"
              title="Fix broken references before deploying"
            >
              Cannot Deploy
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatItem({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: 'green' | 'red';
}) {
  const colorClass = highlight === 'red' ? 'text-red-600' : highlight === 'green' ? 'text-green-600' : 'text-gray-900';
  
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className={`text-xl font-bold ${colorClass}`}>{value}</div>
      <div className="text-xs text-gray-600">{label}</div>
    </div>
  );
}
